import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../utils/useAuth";
import {
  mybookingAPi,
  mybookingTourAPi,
} from "../../../../api/website/hotel/hotelApi";
import Pagination from "@mui/material/Pagination";
import axiosInstance from "../../../../axiosInstance";
import toast from "react-hot-toast";
import { FiDownload, FiCalendar, FiMapPin, FiCreditCard } from "react-icons/fi";

const Badge = ({ ok, okText, noText }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
      ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
    }`}
  >
    {ok ? okText : noText}
  </span>
);

const STATUS_MAP = {
  1: { label: "Ordered", cls: "bg-green-100 text-green-700" },
  2: { label: "Refunded", cls: "bg-blue-100 text-blue-700" },
  3: { label: "Cancelled", cls: "bg-rose-100 text-rose-600" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[Number(status)] || STATUS_MAP[1];
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}
    >
      {s.label}
    </span>
  );
};

const money = (v) => `৳${Number(v || 0).toFixed(0)}`;

const AmountRow = ({ label, value, strong }) => (
  <div
    className={`flex justify-between text-sm ${
      strong ? "font-semibold text-gray-800" : "text-gray-600"
    }`}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
      <FiCalendar className="text-2xl text-gray-400" />
    </div>
    <p className="text-gray-500 text-sm">{text}</p>
  </div>
);

const Bookings = () => {
  const [TourData, setTourData] = useState([]);
  const [Data, setData] = useState([]);
  const [Page, setPage] = useState(1);
  const [Limit, setLimit] = useState(5);
  const [PageData, setPageData] = useState("");
  const [Tab, setTab] = useState(1);
  const { user } = useAuth();
  const orderedBy = user.id;

  useEffect(() => {
    if (Tab === 1) {
      mybookingAPi(setData, orderedBy, Page, Limit, setPageData);
    } else {
      mybookingTourAPi(setTourData, orderedBy, Page, Limit, setPageData);
    }
  }, [orderedBy, Page, Limit, setPageData, Tab]);

  const handleDownloadInvoice = async (item) => {
    const invoiceId = item.invoiceId;
    try {
      const res = await axiosInstance.get(`/booking/download/${invoiceId}`, {
        responseType: "blob",
      });

      const blob = res.data;

      // server pathing an error? it comes back as JSON even with responseType blob
      const isPdf =
        blob.type === "application/pdf" ||
        blob.type === "application/octet-stream";

      if (!isPdf) {
        // read the JSON error message out of the blob
        const text = await blob.text();
        let msg = "Can't download";
        try {
          msg = JSON.parse(text)?.message || msg;
        } catch (_) {}
        toast.error(msg);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
    }
  };

  const handleChangeTab = (tab) => {
    setPage(1);
    setPageData("");
    setTab(tab);
  };

  const handlePay = async (item) => {
    const totalAmount = item.discountedAmount + item.taxAmount;
    const paidAmount = totalAmount * (item.advancePayment / 100);
    try {
      const res = await fetch("https://api.dhoorbin.com/hotel/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: item.orderId,
          invoiceId: item.invoiceId,
          transactionId: item.transactionId,
          hotelName: item.hotelName,
          totalAmount,
          paidAmount,
          primaryPhoneNumber: item.primaryPhoneNumber,
          fullName: user.fullName,
          email: user.email,
        }),
      });
      const data = await res.json();
      if (!data.GatewayPageURL) {
        toast.error("Please try again.");
        return;
      }
      window.location.href = data.GatewayPageURL;
    } catch (error) {
      console.error(error);
      toast.error("Payment failed. Please try again.");
    }
  };

  const list = Tab === 1 ? Data : TourData;

  return (
    <div className="space-y-5">
      {/* header + tabs */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <p className="font-bold uppercase text-sm text-gray-600">My Bookings</p>
        <div className="inline-flex p-1 bg-gray-100 rounded-lg">
          {[
            { id: 1, label: "Hotel" },
            { id: 2, label: "Tour" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => handleChangeTab(t.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium duration-200 ${
                Tab === t.id
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* HOTEL */}
      {Tab === 1 &&
        (Data.length > 0 ? (
          <div className="space-y-4">
            {Data.map((item, index) => {
              const grand =
                (Number(item.discountedAmount) || 0) +
                (Number(item.taxAmount) || 0);
              const due = grand - (Number(item.paidAmount) || 0);
              const fullyDue = due === grand;
              return (
                <div
                  key={index}
                  className="rounded-xl border bg-white overflow-hidden hover:shadow-md duration-200"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3">
                    {/* left */}
                    <div className="lg:col-span-2 p-4 flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-40 h-32 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                        <img
                          className="w-full h-full object-cover"
                          src={item?.rooms[0]?.imageLink[0]}
                          alt=""
                        />
                      </div>
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">
                            {item.invoiceId}
                          </span>
                          <Badge
                            ok={item.paymentNature === "Online"}
                            okText="Online"
                            noText="Offline"
                          />
                          <Badge
                            ok={item.paymentStatus === "Paid"}
                            okText="Paid"
                            noText="Not Paid"
                          />
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-lg font-semibold text-gray-800 truncate">
                          {item.hotelName}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.rooms.map((r, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs"
                            >
                              <FiMapPin className="text-[10px]" />
                              {r.roomName}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-6 text-xs text-gray-500 pt-1">
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide">
                              Check In
                            </span>
                            {item.checkIn}
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide">
                              Check Out
                            </span>
                            {item.checkOut}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* right - amounts */}
                    <div className="p-4 bg-gray-50 border-t lg:border-t-0 lg:border-l flex flex-col justify-between">
                      <div className="space-y-2">
                        <AmountRow
                          label="Discounted"
                          value={money(item.discountedAmount)}
                        />
                        <AmountRow
                          label="Service Charge"
                          value={money(item.taxAmount)}
                        />
                        <AmountRow
                          label="Paid"
                          value={money(item.paidAmount)}
                        />
                        <div className="border-t pt-2">
                          <AmountRow
                            label="Total"
                            value={money(grand)}
                            strong
                          />
                        </div>
                        {due > 0 && (
                          <div className="flex justify-between text-sm font-semibold text-rose-600">
                            <span>Due</span>
                            <span>{money(due)}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 pt-4">
                        <button
                          onClick={() => handleDownloadInvoice(item)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-white duration-200"
                        >
                          <FiDownload /> Invoice
                        </button>
                        {fullyDue && (
                          <button
                            onClick={() => handlePay(item)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 duration-200"
                          >
                            <FiCreditCard /> Pay Now
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState text="You have not booked any hotels or property yet." />
        ))}

      {/* TOUR */}
      {Tab === 2 &&
        (TourData.length > 0 ? (
          <div className="space-y-4">
            {TourData.map((item, index) => {
              const discounted =
                item.discount > 0
                  ? (item.actualPrice || 0) -
                    ((item.actualPrice || 0) * (item.discount || 0)) / 100
                  : 0;
              return (
                <div
                  key={index}
                  className="rounded-xl border bg-white overflow-hidden hover:shadow-md duration-200"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3">
                    <div className="lg:col-span-2 p-4 flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-40 h-32 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                        <img
                          className="w-full h-full object-cover"
                          src={item?.tourImage}
                          alt=""
                        />
                      </div>
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">
                            {item.invoiceId}
                          </span>
                          <Badge
                            ok={item.paymentNature === "Full"}
                            okText="Fully"
                            noText="Partially"
                          />
                          <Badge
                            ok={item.paymentStatus === "Paid"}
                            okText="Paid"
                            noText="Not Paid"
                          />
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-lg font-semibold text-gray-800 truncate">
                          {item.tourName}
                        </p>
                        <div className="flex gap-6 text-xs text-gray-500 pt-1">
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide">
                              Start Date
                            </span>
                            {item.startDate?.split("T")[0]}
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase tracking-wide">
                              End Date
                            </span>
                            {item.endDate?.split("T")[0]}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 border-t lg:border-t-0 lg:border-l flex flex-col justify-between">
                      <div className="space-y-2">
                        <AmountRow
                          label="Actual Amount"
                          value={money(item.actualPrice)}
                        />
                        {item.discount !== 0 && (
                          <AmountRow
                            label="Discounted"
                            value={money(discounted)}
                          />
                        )}
                        <AmountRow
                          label="Paid"
                          value={money(item.totalAmount)}
                        />
                        <div className="border-t pt-2">
                          <AmountRow
                            label="Total"
                            value={money(item.totalAmount)}
                            strong
                          />
                        </div>
                      </div>
                      <div className="pt-4">
                        <button
                          onClick={() => handleDownloadInvoice(item)}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-white duration-200"
                        >
                          <FiDownload /> Invoice
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState text="You have not booked any tour yet." />
        ))}

      {/* pagination */}
      {list.length > 0 && (
        <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
          <p className="text-xs text-gray-500">
            Showing {(Page - 1) * Limit + 1} to{" "}
            {(Page - 1) * Limit + list.length} of {PageData} entries
          </p>
          <Pagination
            count={PageData || 1}
            page={Page}
            onChange={(event, value) => setPage(value)}
          />
        </div>
      )}
    </div>
  );
};

export default Bookings;
